import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from 'prop-types';
import { DatePicker } from "antd";
import { Info, PlusCircle, X } from 'feather-icons-react/build/IconComponents';
import { useDispatch } from "react-redux";
import { productActions } from "../../core/action/productActions"; // FIXED: Import from correct file
import Swal from 'sweetalert2';
import moment from 'moment';

const AddProductModal = ({
    isOpen,
    onClose,
    onSuccess,
    isEditMode = false,
    productId = null,
    productData = null
}) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    //const [fetchingData, setFetchingData] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        productName: '',
        hsnCode: '',
        sku: '',
        itemCode: '',
        costPrice: '',
        sellingPrice: '',
        category: null,
        brand: null,
        unit: null,
        barcodeSymbology: null,
        description: '',
        weight: '',
        dimensions: { length: '', width: '', height: '' },
        status: 'active',
        images: [],
        hasWarranty: false,
        warrantyPeriod: '',
        warrantyType: null,
        warrantyTerms: '',
        isManufacturer: false,
        hasExpiry: false,
        manufacturedDate: null,
        expiryDate: null,
    });

    // Master data
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [warrantyTypeOptions, setWarrantyTypeOptions] = useState([]);

    // Options for selects
    const barcodesymbol = [
        { value: "Code128", label: "Code128" },
        { value: "Code39", label: "Code39" },
        { value: "EAN-13", label: "EAN-13" },
    ];

    const warrantyTypeOptionsData = [
        { value: 'manufacturer', label: 'Manufacturer Warranty' },
        { value: 'seller', label: 'Seller Warranty' },
        { value: 'extended', label: 'Extended Warranty' },
        { value: 'international', label: 'International Warranty' },
    ];

    // Handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            let updatedForm = { ...prev };

            if (type === 'checkbox') {
                updatedForm[name] = checked;

                if (name === 'hasWarranty' && !checked) {
                    updatedForm.warrantyPeriod = '';
                    updatedForm.warrantyType = null;
                    updatedForm.warrantyTerms = '';
                }
                if (name === 'isManufacturer' && !checked) {
                    updatedForm.manufacturedDate = null;
                }
                if (name === 'hasExpiry' && !checked) {
                    updatedForm.expiryDate = null;
                }
            } else {
                updatedForm[name] = value;
            }

            if (name.startsWith('dimensions.')) {
                const dimensionKey = name.split('.')[1];
                updatedForm = {
                    ...updatedForm,
                    dimensions: {
                        ...prev.dimensions,
                        [dimensionKey]: value
                    }
                };
            }

            return updatedForm;
        });
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption
        }));
    };

    const handleDateChange = (date, name) => {
        try {
            setFormData(prev => ({
                ...prev,
                [name]: date
            }));
        } catch (error) {
            console.error('Error in handleDateChange:', error);
            Swal.fire('Error', 'Invalid date selected', 'error');
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const previewImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            isNew: true
        }));

        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...previewImages]
        }));
    };

    const handleRemoveImage = (index) => {
        const newImages = [...formData.images];
        newImages.splice(index, 1);
        setFormData(prev => ({ ...prev, images: newImages }));
    };

    // Generate unique SKU
    const generateUniqueSku = async () => {
        try {
            const year = new Date().getFullYear().toString().slice(-2);
            const generateRandomPart = () => {
                const letters = Math.random().toString(36).substring(2, 4).toUpperCase();
                const digits = Math.floor(10 + Math.random() * 90);
                return `${letters}${digits}`;
            };

            let candidate = "";
            let attempts = 0;

            while (attempts < 10) {
                candidate = `SKU${year}-${generateRandomPart()}`;
                const exists = await productActions.checkSkuExists(candidate);
                if (!exists) break;
                attempts++;
            }

            if (attempts === 10) {
                Swal.fire('Error', "Couldn't generate a unique SKU. Try again.", 'error');
                return;
            }

            setFormData(prev => ({ ...prev, sku: candidate }));
        } catch (error) {
            console.error('Error generating SKU:', error);
            Swal.fire('Error', 'Failed to generate SKU', 'error');
        }
    };

    // Generate unique Item Code
    const generateUniqueItemCode = async () => {
        try {
            const year = new Date().getFullYear().toString().slice(-2);
            const generateRandomPart = () => {
                const letters = Math.random().toString(36).substring(2, 4).toUpperCase();
                const digits = Math.floor(10 + Math.random() * 90);
                return `${letters}${digits}`;
            };

            let candidate = "";
            let attempts = 0;

            while (attempts < 10) {
                candidate = `ITM${year}-${generateRandomPart()}`;
                const exists = await productActions.checkItemCodeExists(candidate);
                if (!exists) break;
                attempts++;
            }

            if (attempts === 10) {
                Swal.fire('Error', "Couldn't generate a unique Item Code. Try again.", 'error');
                return;
            }

            setFormData(prev => ({ ...prev, itemCode: candidate }));
        } catch (error) {
            console.error('Error generating Item Code:', error);
            Swal.fire('Error', 'Failed to generate Item Code', 'error');
        }
    };

    // Validate form
    const validateForm = () => {
        if (!formData.productName || formData.productName.trim() === '') {
            Swal.fire('Error', 'Please enter product name', 'error');
            return false;
        }

        if (isEditMode) {
            if (formData.costPrice === '' || Number.isNaN(parseFloat(formData.costPrice)) || parseFloat(formData.costPrice) < 0) {
                Swal.fire('Error', 'Please enter valid cost price', 'error');
                return false;
            }

            if (formData.sellingPrice === '' || Number.isNaN(parseFloat(formData.sellingPrice)) || parseFloat(formData.sellingPrice) < 0) {
                Swal.fire('Error', 'Please enter valid selling price', 'error');
                return false;
            }

            return true;
        }

        if (!formData.sku || formData.sku.trim() === '') {
            Swal.fire('Error', 'Please enter SKU', 'error');
            return false;
        }

        if (!formData.category) {
            Swal.fire('Error', 'Please select category', 'error');
            return false;
        }

        if (!formData.unit) {
            Swal.fire('Error', 'Please select unit', 'error');
            return false;
        }

        if (formData.hasWarranty && (!formData.warrantyPeriod || formData.warrantyPeriod.trim() === '')) {
            Swal.fire('Error', 'Please enter warranty period', 'error');
            return false;
        }

        return true;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            const productDataToSend = {
                product_name: formData.productName.trim(),
                hsn_code: formData.hsnCode || '',
                sku: formData.sku.trim(),
                item_code: formData.itemCode || '',
                category_id: formData.category?.value ? parseInt(formData.category.value) : null,
                brand_id: formData.brand?.value ? parseInt(formData.brand.value) : null,
                unit_id: formData.unit?.value ? parseInt(formData.unit.value) : null,
                barcode_symbology: formData.barcodeSymbology?.value || '',
                description: formData.description || '',
                weight: parseFloat(formData.weight) || 0,
                dimensions: JSON.stringify(formData.dimensions),
                has_warranty: formData.hasWarranty ? 1 : 0,
                warranty_period: formData.warrantyPeriod || '',
                warranty_type: formData.warrantyType?.value || '',
                warranty_terms: formData.warrantyTerms || '',
                has_manufacturer: formData.isManufacturer ? 1 : 0,
                has_expiry: formData.hasExpiry ? 1 : 0,
                manufactured_date: formData.manufacturedDate ?
                    moment(formData.manufacturedDate).format('YYYY-MM-DD') : null,
                expiry_date: formData.expiryDate ?
                    moment(formData.expiryDate).format('YYYY-MM-DD') : null,
                status: formData.status
            };

            console.log('Sending product data:', productDataToSend);

            let result;

            if (isEditMode && productId) {
                result = await dispatch(productActions.updateProduct(productId, {
                    ...productDataToSend,
                    cost_price: parseFloat(formData.costPrice || 0),
                    selling_price: parseFloat(formData.sellingPrice || 0),
                    user_role: user?.role_slug || ''
                }));
            } else {
                productDataToSend.images = formData.images.map(img => img.file);
                result = await dispatch(productActions.addProductList(productDataToSend));
            }

            if (result?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: `Product ${isEditMode ? 'updated' : 'created'} successfully`,
                    confirmButtonColor: '#00ff00',
                    timer: 1500,
                    showConfirmButton: false
                });

                resetForm();
                onSuccess?.();
                onClose();
            } else {
                throw new Error(result?.message || 'Failed to save product');
            }
        } catch (error) {
            console.error('Failed to save product:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.message || `Failed to ${isEditMode ? 'update' : 'create'} product`,
                confirmButtonColor: '#ff0000'
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            productName: '',
            hsnCode: '',
            sku: '',
            itemCode: '',
            costPrice: '',
            sellingPrice: '',
            category: null,
            brand: null,
            unit: null,
            barcodeSymbology: null,
            description: '',
            weight: '',
            dimensions: { length: '', width: '', height: '' },
            status: 'active',
            images: [],
            hasWarranty: false,
            warrantyPeriod: '',
            warrantyType: null,
            warrantyTerms: '',
            isManufacturer: false,
            hasExpiry: false,
            manufacturedDate: null,
            expiryDate: null,
        });
    };

    // Load master data
    const loadMasterData = async () => {
        try {
            const result = await dispatch(productActions.getMasterData());
            console.log('Master data result:', result);

            if (result.success) {
                const { categories, brands, units } = result.data;

                setCategoryOptions(categories.map(cat => ({
                    value: cat.id,
                    label: cat.category_name || cat.Category || cat.name
                })));

                setBrandOptions(brands.map(brand => ({
                    value: brand.id,
                    label: brand.brand_name || brand.Brand || brand.name
                })));

                setUnitOptions(units.map(unit => ({
                    value: unit.id,
                    label: unit.unit_name || unit.Unit || unit.name
                })));

                setWarrantyTypeOptions(warrantyTypeOptionsData);
            } else {
                console.error('Failed to load master data:', result.message);
            }
        } catch (error) {
            console.error('Error loading master data:', error);
        }
    };

    // Load product data for editing
    const loadEditData = () => {
        if (!isEditMode) return;

        if (productData) {
            console.log('Loading product data for edit:', productData);

            // Get the actual product data (could be nested one or two levels deep)
            const product = productData?.data?.data || productData?.data || productData || {};

            const parsedCostPrice = parseFloat(product.cost_price ?? product.purchase_price ?? 0);
            const parsedSellingPrice = parseFloat(product.selling_price ?? product.price ?? 0);

            // Format dates
            let manufacturedDate = null;
            let expiryDate = null;

            if (product.manufactured_date) {
                manufacturedDate = moment(product.manufactured_date);
            }

            if (product.expiry_date) {
                expiryDate = moment(product.expiry_date);
            }

            // Parse dimensions
            let dimensions = { length: '', width: '', height: '' };
            if (product.dimensions) {
                if (typeof product.dimensions === 'string') {
                    try {
                        dimensions = JSON.parse(product.dimensions);
                    } catch (error) {
                        console.error('Error parsing dimensions:', error);
                    }
                } else if (typeof product.dimensions === 'object') {
                    dimensions = product.dimensions;
                }
            }

            // Prepare barcode symbology
            let barcodeSymbology = null;
            if (product.barcode_symbology) {
                barcodeSymbology = barcodesymbol.find(
                    opt => opt.value === product.barcode_symbology
                ) || { value: product.barcode_symbology, label: product.barcode_symbology };
            }

            // Prepare warranty type
            let warrantyType = null;
            if (product.warranty_type) {
                warrantyType = warrantyTypeOptionsData.find(
                    opt => opt.value === product.warranty_type
                ) || { value: product.warranty_type, label: product.warranty_type };
            }

            // Prepare category option
            let categoryOption = null;
            if (product.category_id) {
                categoryOption = {
                    value: product.category_id,
                    label: product.category_name || product.category || ''
                };
            }

            // Prepare brand option
            let brandOption = null;
            if (product.brand_id) {
                brandOption = {
                    value: product.brand_id,
                    label: product.brand_name || product.brand || ''
                };
            }

            // Prepare unit option
            let unitOption = null;
            if (product.unit_id) {
                unitOption = {
                    value: product.unit_id,
                    label: product.unit_name || product.unit || ''
                };
            }

            // Load images
            let images = [];
            if (product.images && Array.isArray(product.images)) {
                images = product.images.map(img => ({
                    preview: img.path || img,
                    isNew: false
                }));
            }

            setFormData({
                productName: product.product_name || '',
                hsnCode: product.hsn_code || '',
                sku: product.sku || '',
                itemCode: product.item_code || '',
                costPrice: Number.isNaN(parsedCostPrice) ? 0 : parsedCostPrice,
                sellingPrice: Number.isNaN(parsedSellingPrice) ? 0 : parsedSellingPrice,
                category: categoryOption,
                brand: brandOption,
                unit: unitOption,
                barcodeSymbology: barcodeSymbology,
                description: product.description || '',
                weight: product.weight || '',
                dimensions: dimensions,
                status: product.status || 'active',
                images: images,
                hasWarranty: Boolean(product.has_warranty),
                warrantyPeriod: product.warranty_period || '',
                warrantyType: warrantyType,
                warrantyTerms: product.warranty_terms || '',
                isManufacturer: Boolean(product.has_manufacturer),
                hasExpiry: Boolean(product.has_expiry),
                manufacturedDate: manufacturedDate,
                expiryDate: expiryDate,
            });
        }
    };

    // Handle modal close
    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Load data when modal opens
    useEffect(() => {
        if (isOpen) {
            loadMasterData();

            if (isEditMode && productData) {
                loadEditData();
            } else if (!isEditMode) {
                resetForm();
                generateUniqueSku();
                generateUniqueItemCode();
            }
        }
    }, [isOpen, isEditMode, productData]);

    if (!isOpen) return null;

    return (
        <div
            className="modal-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1040,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
        >
            <div
                className="modal-content"
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    width: '100%',
                    maxWidth: '1000px',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header" style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa'
                }}>
                    <h5 className="modal-title" style={{ margin: 0, fontWeight: '600' }}>
                        {isEditMode ? 'Edit Product' : 'Add New Product'}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        disabled={loading}
                        style={{
                            border: 'none',
                            background: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6c757d'
                        }}
                    >
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{
                    padding: '1.5rem',
                    overflowY: 'auto',
                    flex: 1
                }}>
                    <form onSubmit={handleSubmit}>
                        <div className="add-product pb-0">
                            {/* Product Information */}
                            <div className="accordion-card-one accordion" id="accordionExample">
                                <div className="accordion-item">
                                    <div className="accordion-header" id="headingOne">
                                        <div className="accordion-button" data-bs-toggle="collapse" data-bs-target="#collapseOne">
                                            <div className="addproduct-icon">
                                                <h5>
                                                    <Info className="add-info" />
                                                    <span>Product Information</span>
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div id="collapseOne" className="accordion-collapse collapse show">
                                        <div className="accordion-body">
                                            <div className="row">
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <label className="form-label">Product Name *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="productName"
                                                            value={formData.productName}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <label className="form-label">HSN Code</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="hsnCode"
                                                            value={formData.hsnCode}
                                                            onChange={handleInputChange}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product list">
                                                        <label>SKU *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control list"
                                                            placeholder="Enter SKU"
                                                            name="sku"
                                                            value={formData.sku}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={loading || isEditMode}
                                                        />
                                                        {!isEditMode && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primaryadd"
                                                                onClick={generateUniqueSku}
                                                                disabled={loading}
                                                            >
                                                                Generate
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product list">
                                                        <label>Item Code</label>
                                                        <input
                                                            type="text"
                                                            className="form-control list"
                                                            placeholder="Please Enter Item Code"
                                                            name="itemCode"
                                                            value={formData.itemCode}
                                                            onChange={handleInputChange}
                                                            disabled={loading || isEditMode}
                                                        />
                                                        {!isEditMode && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primaryadd"
                                                                onClick={generateUniqueItemCode}
                                                                disabled={loading}
                                                            >
                                                                Generate
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <label>Status</label>
                                                        <select
                                                            className="form-control"
                                                            name="status"
                                                            value={formData.status}
                                                            onChange={handleInputChange}
                                                            disabled={loading}
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                            <option value="draft">Draft</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <div className="add-newplus">
                                                            <label className="form-label">Brand</label>
                                                        </div>
                                                        <Select
                                                            className="select"
                                                            options={brandOptions}
                                                            placeholder="Choose Brand"
                                                            value={formData.brand}
                                                            onChange={(selected) => handleSelectChange('brand', selected)}
                                                            isDisabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <div className="add-newplus">
                                                            <label className="form-label">Category *</label>
                                                        </div>
                                                        <Select
                                                            className="select"
                                                            options={categoryOptions}
                                                            placeholder="Choose Category"
                                                            value={formData.category}
                                                            onChange={(selected) => handleSelectChange('category', selected)}
                                                            isDisabled={loading}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <label className="form-label">Unit *</label>
                                                        <Select
                                                            className="select"
                                                            options={unitOptions}
                                                            placeholder="Choose Unit"
                                                            value={formData.unit}
                                                            onChange={(selected) => handleSelectChange('unit', selected)}
                                                            isDisabled={loading}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product">
                                                        <label>Cost Price *</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="costPrice"
                                                            value={formData.costPrice}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                            step="0.01"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product">
                                                        <label>Selling Price *</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="sellingPrice"
                                                            value={formData.sellingPrice}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                            step="0.01"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product">
                                                        <label>Weight (kg)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="weight"
                                                            value={formData.weight}
                                                            onChange={handleInputChange}

                                                            min="0"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks add-product">
                                                        <label>Dimensions (L x W x H cm)</label>
                                                        <div className="row g-2">
                                                            <div className="col-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    placeholder="L"
                                                                    name="dimensions.length"
                                                                    value={formData.dimensions.length}
                                                                    onChange={handleInputChange}
                                                                    step="0.1"
                                                                    min="0"
                                                                    disabled={loading}
                                                                />
                                                            </div>
                                                            <div className="col-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    placeholder="W"
                                                                    name="dimensions.width"
                                                                    value={formData.dimensions.width}
                                                                    onChange={handleInputChange}
                                                                    step="0.1"
                                                                    min="0"
                                                                    disabled={loading}
                                                                />
                                                            </div>
                                                            <div className="col-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    placeholder="H"
                                                                    name="dimensions.height"
                                                                    value={formData.dimensions.height}
                                                                    onChange={handleInputChange}
                                                                    step="0.1"
                                                                    min="0"
                                                                    disabled={loading}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="mb-3 add-product">
                                                        <label className="form-label">Barcode Symbology</label>
                                                        <Select
                                                            className="select"
                                                            options={barcodesymbol}
                                                            placeholder="Choose"
                                                            value={formData.barcodeSymbology}
                                                            onChange={(selected) => handleSelectChange('barcodeSymbology', selected)}
                                                            isDisabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-lg-12">
                                                <div className="input-blocks summer-description-box transfer mb-3">
                                                    <label>Description</label>
                                                    <textarea
                                                        className="form-control h-100"
                                                        rows={5}
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleInputChange}
                                                        disabled={loading}
                                                    />
                                                    <p className="mt-1">Maximum 60 Characters</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Images Section */}
                            <div className="accordion-card-one accordion" id="accordionExample3">
                                <div className="accordion-item">
                                    <div className="accordion-header" id="headingThree">
                                        <div className="accordion-button" data-bs-toggle="collapse" data-bs-target="#collapseThree">
                                            <div className="addproduct-icon list">
                                                <h5>
                                                    <i className="feather-image add-info" />
                                                    <span>Images</span>
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div id="collapseThree" className="accordion-collapse collapse show">
                                        <div className="accordion-body">
                                            <div className="col-lg-12">
                                                <div className="add-choosen">
                                                    <div className="input-blocks">
                                                        <div className="image-upload">
                                                            <input
                                                                type="file"
                                                                onChange={handleImageUpload}
                                                                multiple
                                                                accept="image/*"
                                                                disabled={loading}
                                                            />
                                                            <div className="image-uploads">
                                                                <PlusCircle className="plus-down-add me-0" />
                                                                <h4>Add Images</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {formData.images.map((img, index) => (
                                                        <div className="phone-img" key={index} style={{ position: 'relative', display: 'inline-block', margin: '5px' }}>
                                                            <img
                                                                src={img.preview || img}
                                                                alt={`uploaded-${index}`}
                                                                width={100}
                                                                height={100}
                                                                style={{ objectFit: 'cover' }}
                                                                onError={(e) => {
                                                                    e.target.src = 'https://via.placeholder.com/100x100?text=Image+Error';
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleRemoveImage(index)}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '5px',
                                                                    right: '5px',
                                                                    padding: '2px 5px',
                                                                    fontSize: '12px'
                                                                }}
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Custom Fields */}
                            <div className="accordion-card-one accordion" id="accordionExample4">
                                <div className="accordion-item">
                                    <div className="accordion-header" id="headingFour">
                                        <div className="accordion-button" data-bs-toggle="collapse" data-bs-target="#collapseFour">
                                            <div className="addproduct-icon list">
                                                <h5>
                                                    <span>Custom Fields</span>
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div id="collapseFour" className="accordion-collapse collapse show">
                                        <div className="accordion-body">
                                            <div className="custom-filed">
                                                <div className="input-block add-lists">
                                                    <label className="checkboxs">
                                                        <input
                                                            type="checkbox"
                                                            name="hasWarranty"
                                                            checked={formData.hasWarranty}
                                                            onChange={handleInputChange}
                                                            disabled={loading}
                                                        />
                                                        <span className="checkmarks" />
                                                        Warranties
                                                    </label>
                                                    <label className="checkboxs">
                                                        <input
                                                            type="checkbox"
                                                            name="isManufacturer"
                                                            checked={formData.isManufacturer}
                                                            onChange={handleInputChange}
                                                            disabled={loading}
                                                        />
                                                        <span className="checkmarks" />
                                                        Manufacturer
                                                    </label>
                                                    <label className="checkboxs">
                                                        <input
                                                            type="checkbox"
                                                            name="hasExpiry"
                                                            checked={formData.hasExpiry}
                                                            onChange={handleInputChange}
                                                            disabled={loading}
                                                        />
                                                        <span className="checkmarks" />
                                                        Expiry
                                                    </label>
                                                </div>
                                            </div>

                                            {formData.hasWarranty && (
                                                <div className="row mt-3">
                                                    <div className="col-lg-12">
                                                        <div className="card">
                                                            <div className="card-body">
                                                                <div className="row">
                                                                    <div className="col-lg-4 col-sm-6 col-12">
                                                                        <div className="input-blocks">
                                                                            <label>Warranty Period *</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                name="warrantyPeriod"
                                                                                value={formData.warrantyPeriod}
                                                                                onChange={handleInputChange}
                                                                                placeholder="e.g., 1 Year, 6 Months"
                                                                                required={formData.hasWarranty}
                                                                                disabled={loading}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-4 col-sm-6 col-12">
                                                                        <div className="input-blocks">
                                                                            <label>Warranty Type</label>
                                                                            <Select
                                                                                className="select"
                                                                                options={warrantyTypeOptions}
                                                                                placeholder="Select Warranty Type"
                                                                                value={formData.warrantyType}
                                                                                onChange={(selected) => handleSelectChange('warrantyType', selected)}
                                                                                isDisabled={loading}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-4 col-sm-6 col-12">
                                                                        <div className="input-blocks">
                                                                            <label>Warranty Terms</label>
                                                                            <textarea
                                                                                className="form-control"
                                                                                rows={2}
                                                                                name="warrantyTerms"
                                                                                value={formData.warrantyTerms}
                                                                                onChange={handleInputChange}
                                                                                placeholder="Enter warranty terms and conditions"
                                                                                disabled={loading}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="row mt-3">
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <label>Manufactured Date</label>
                                                        <div className="input-groupicon calender-input">
                                                            <DatePicker
                                                                selected={formData.manufacturedDate}
                                                                onChange={(date) => handleDateChange(date, 'manufacturedDate')}
                                                                className="form-control"
                                                                dateFormat="dd-MM-yyyy"
                                                                placeholderText="Choose Date"
                                                                disabled={!formData.isManufacturer || loading}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <label>Expiry Date</label>
                                                        <div className="input-groupicon calender-input">
                                                            <DatePicker
                                                                selected={formData.expiryDate}
                                                                onChange={(date) => handleDateChange(date, 'expiryDate')}
                                                                className="form-control"
                                                                dateFormat="dd-MM-yyyy"
                                                                placeholderText="Choose Date"
                                                                disabled={!formData.hasExpiry || loading}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="modal-footer" style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                    backgroundColor: '#f8f9fa'
                }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                {isEditMode ? 'Updating...' : 'Saving...'}
                            </>
                        ) : (
                            isEditMode ? 'Update Product' : 'Save Product'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

AddProductModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    isEditMode: PropTypes.bool,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productData: PropTypes.object
};

export default AddProductModal;